/**
 * Distinct service-level mechanics on a work order (#3092 / PF-048).
 *
 * The schedule bar still has one booking-level mechanic select. When services
 * are split across people, this row of chips is the extra UI so staff can see
 * every assignee. One mechanic (or none) keeps the existing single select.
 */

import { Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ServiceMechanicRef {
  assignedMechanicId?: string | null;
  mechanicId?: string | null;
  mechanic?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export interface DistinctServiceMechanic {
  mechanicId: string | null;
  count: number;
}

export interface MechanicNameLookup {
  id: string;
  firstName: string;
  lastName: string;
}

export interface WorkOrderMechanicsSummaryProps {
  services: readonly ServiceMechanicRef[];
  mechanics?: readonly MechanicNameLookup[];
  mechanicNames?: Record<string, string>;
}

function serviceAssigneeId(service: ServiceMechanicRef): string | null {
  const raw = service.assignedMechanicId ?? service.mechanicId ?? null;
  if (raw == null || raw === '') return null;
  return raw;
}

function formatPersonName(person: {
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
}

/** Distinct assignees in first-seen order, with a per-mechanic service count. */
export function distinctServiceMechanics(
  services: readonly ServiceMechanicRef[],
): DistinctServiceMechanic[] {
  const counts = new Map<string | null, number>();
  const order: Array<string | null> = [];
  for (const service of services) {
    const mechanicId = serviceAssigneeId(service);
    const prev = counts.get(mechanicId);
    if (prev === undefined) {
      order.push(mechanicId);
      counts.set(mechanicId, 1);
    } else {
      counts.set(mechanicId, prev + 1);
    }
  }
  return order.map((mechanicId) => ({
    mechanicId,
    count: counts.get(mechanicId) ?? 0,
  }));
}

function resolveMechanicName(
  mechanicId: string,
  services: readonly ServiceMechanicRef[],
  mechanics: readonly MechanicNameLookup[] | undefined,
  mechanicNames: Record<string, string> | undefined,
): string {
  for (const service of services) {
    if (serviceAssigneeId(service) !== mechanicId) continue;
    if (service.mechanic) {
      const name = formatPersonName(service.mechanic);
      if (name) return name;
    }
  }
  const fromList = mechanics?.find((mechanic) => mechanic.id === mechanicId);
  if (fromList) {
    const name = formatPersonName(fromList);
    if (name) return name;
  }
  const fromMap = mechanicNames?.[mechanicId];
  if (fromMap) return fromMap;
  return mechanicId;
}

export function WorkOrderMechanicsSummary({
  services,
  mechanics,
  mechanicNames,
}: WorkOrderMechanicsSummaryProps) {
  const { t } = useTranslation('workorders');
  const distinct = distinctServiceMechanics(services);
  if (distinct.length <= 1) return null;

  return (
    <Stack
      direction="row"
      useFlexGap
      spacing={0.5}
      flexWrap="wrap"
      sx={{ mt: 0.5 }}
      data-testid="work-order-mechanics-summary"
    >
      {distinct.map((entry) => {
        const name = entry.mechanicId
          ? resolveMechanicName(entry.mechanicId, services, mechanics, mechanicNames)
          : t('mechanicsSummary.unassigned');
        return (
          <Chip
            key={entry.mechanicId ?? 'unassigned'}
            size="small"
            variant="outlined"
            label={`${name} · ${t('mechanicsSummary.services', { count: entry.count })}`}
          />
        );
      })}
    </Stack>
  );
}
